import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowBackRounded } from "@mui/icons-material";
import { GoAlert, GoFileBinary, GoGoal, GoSearch } from "react-icons/go";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

const POLL_INTERVAL = 10_000;

const clampPercentage = value => {
    if (value == null) return null;
    const number = Number(value);

    if (!Number.isFinite(number)) {
        return 0;
    }

    return Math.min(100, Math.max(0, number));
};

export default function ScanProgressPreview({
    api,
    request,
    paymentAccepted = false,
    onPaymentNoticeShown,
    onBack,
    onViewScan,
}) {
    const { t } = useTranslation();

    const [progress, setProgress] = useState(null);
    const [error, setError] = useState(null);
    const [retry, setRetry] = useState(0);
    const reducedMotion = useReducedMotion();
    const requestId = request?.request_id;
    const progressUrl = request?.progress_url;

    useEffect(() => {
        if (!requestId) {
            setError(new Error("Identifiant de demande manquant."));
            return;
        }

        const controller = new AbortController();
        let timer = null;

        const loadProgress = async () => {
            try {
                const data = await api.progress(
                    requestId,
                    controller.signal,
                    progressUrl
                );

                if (controller.signal.aborted) {
                    return;
                }

                setProgress(data);
                setError(null);

                if (
                    (["pending_creation", "launching", "pending", "queued", "running"].includes(data.status)
                        || (data.status === "completed" && data.report_available !== true))
                    && !["progress_identity_mismatch", "progress_status_unknown", "legacy_binding_missing"].includes(data.tracking_error?.code)
                ) {
                    timer = window.setTimeout(
                        loadProgress,
                        POLL_INTERVAL
                    );
                }
            } catch (error) {
                if (controller.signal.aborted) {
                    return;
                }

                setError(error);
            }
        };

        loadProgress();

        return () => {
            controller.abort();

            if (timer !== null) {
                window.clearTimeout(timer);
            }
        };
    }, [api, requestId, progressUrl, retry]);

    const percentage = clampPercentage(
        progress?.percentage
    );

    const completed =
        progress?.status === "completed"
        && progress?.report_available === true;

    const failed = ["failed", "launch_failed"].includes(progress?.status);
    const needsReview = ["launch_unknown", "blocked", "unknown"].includes(progress?.status);
    const score = progress?.score ?? request?.score;
    const criticalFindings = progress?.critical_findings
        ?? progress?.critical_count
        ?? progress?.findings?.critical;
    const generatedFiles = progress?.report_available === false
        ? "—"
        : progress?.report_available === true
            ? "PDF"
            : null;
    const target = progress?.target_url ?? request?.target_url ?? request?.data_scan?.url ?? request?.target ?? "—";

    useEffect(() => {
        if (!paymentAccepted) {
            return;
        }

        const timer = window.setTimeout(
            () => onPaymentNoticeShown?.(),
            4_000
        );

        return () => window.clearTimeout(timer);
    }, [paymentAccepted, onPaymentNoticeShown]);

    return (
        <>
            <div className="scan-history-header">
                <button
                    type="button"
                    className="scan-workflow-back-link"
                    onClick={onBack}
                >
                    <ArrowBackRounded aria-hidden="true" />
                    {t("Retour au suivi des scans")}
                </button>
            </div>
            <section className="scan-progress-preview"aria-label={t("Progression du scan")}>
                {paymentAccepted && (
                    <div className="scan-workflow-toast-position">
                        <AnimatePresence>
                            <motion.div
                                className="scan-payment-accepted-toast"
                                role="status"
                                aria-live="polite"
                                initial={{
                                    opacity: 0,
                                    y: reducedMotion ? 0 : -60,
                                }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{
                                    opacity: 0,
                                    y: reducedMotion ? 0 : -24,
                                }}
                                transition={{
                                    duration: reducedMotion ? 0 : 0.3,
                                    ease: "easeOut",
                                }}
                            >
                                <span
                                    className="scan-payment-accepted-toast-icon"
                                    aria-hidden="true"
                                >
                                    ✓
                                </span>
                                <span>
                                    <strong>{t("Paiement accepté")}</strong>
                                    <small>{t("Votre scan démarre automatiquement.")}</small>
                                </span>
                                <button
                                    type="button"
                                    aria-label={t("Fermer")}
                                    onClick={() => onPaymentNoticeShown?.()}
                                >
                                    ×
                                </button>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                )}
                <div className="scan-progress-preview-card">
                    <div className={`scan-progress-preview-status-icon ${completed ? "is-complete" : ""}`} aria-hidden="true">
                        {completed ? "✓" : <GoSearch />}
                    </div>

                    <h2 className="scan-progress-preview-title">
                        {needsReview ? t("Lancement à vérifier") : completed
                            ? t("Analyse DAST terminée avec succès")
                            : failed
                                ? t("Analyse DAST interrompue")
                                : t("Analyse DAST en cours")}
                    </h2>

                    <p className="scan-progress-preview-target">
                        {t("Cible")} : <strong>{target}</strong>
                    </p>

                    {!progress && !error && (
                        <p role="status">
                            {t("Récupération de la progression du scan…")}
                        </p>
                    )}

                    {progress && (
                        <>
                            <div className="scan-progress-preview-heading">
                                <div>
                                    <span>{t("Progression du scan")} :</span>

                                    <strong>
                                        {percentage === null ? "—" : `${Math.round(percentage)} %`}
                                    </strong>
                                </div>
                            </div>
                            <div
                                className="scan-page-progress"
                                role="progressbar"
                                aria-valuemin="0"
                                aria-valuemax="100"
                                aria-valuenow={percentage ?? undefined}
                            >
                                <span
                                    className="scan-page-progress-bar"
                                    style={{
                                        "--scan-progress-width": `${percentage ?? 0}%`,
                                    }}
                                />
                            </div>

                            <div className={`scan-progress-preview-message ${completed ? "is-complete" : ""}`} role="status">
                                <span aria-hidden="true">{completed ? "" : "◌"}</span>
                                {needsReview ? t("Une vérification côté serveur est nécessaire pour retrouver le suivi de ce scan.")
                                    : failed ? t("Le scan a rencontré une erreur.") : completed
                                    ? t("Le scan est complété, votre rapport est disponible.")
                                    : progress.status === "completed" ? t("Analyse terminée, rapport en préparation…")
                                    : progress.status === "pending_creation"
                                        ? t("Initialisation du scan…")
                                        : t("Le scan est en cours d'exécution…")}
                            </div>

                            <div className="scan-progress-preview-metrics">
                                <div className="scan-progress-preview-metric">
                                    <GoGoal aria-hidden="true" />
                                    <span>{t("Score Global")}</span>
                                    <strong>
                                        {score !== null && score !== undefined ? (
                                            `${score} / 100`
                                        ) : (
                                            <span className="scan-loading-bar"></span>
                                        )}
                                    </strong>
                                </div>

                                <div className="scan-progress-preview-metric">
                                    <GoAlert aria-hidden="true" />
                                    <span>{t("Failles Critiques")}</span>
                                    <strong>
                                        {criticalFindings !== null && criticalFindings !== undefined ? (
                                            criticalFindings
                                        ) : (
                                            <span className="scan-loading-bar"></span>
                                        )}
                                    </strong>
                                </div>

                                <div className="scan-progress-preview-metric">
                                    <GoFileBinary aria-hidden="true" />
                                    <span>{t(completed ? "Fichiers Générés" : "Étape actuelle")}</span>
                                    <strong>
                                        {completed ? (
                                            generatedFiles !== null ? generatedFiles : <span className="scan-loading-bar"></span>
                                        ) : (
                                            progress?.stage || <span className="scan-loading-bar"></span>
                                        )}
                                    </strong>
                                </div>
                            </div>

                            {failed && (
                                <div
                                    role="alert"
                                    className="scan-error"
                                >
                                    <p>
                                        {progress.error
                                            || t(
                                                "Le scan a rencontré une erreur."
                                            )}
                                    </p>
                                </div>
                            )}

                            {completed && progress.scan_id && (
                                <div className="scan-progress-preview-complete">
                                    <button
                                        type="button"
                                        className="buttons-ecp buttons-ecp-update"
                                        onClick={() =>
                                            onViewScan(progress)
                                        }
                                    >
                                        {t("Voir le scan")}
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {error && (
                        <div role="alert" className="scan-error">
                            <p>
                                {t(
                                    "Impossible de récupérer la progression du scan."
                                )}
                            </p>

                            <button
                                type="button"
                                className="buttons-ecp buttons-ecp-create"
                                onClick={() =>
                                    setRetry(value => value + 1)
                                }
                            >
                                {t("Réessayer")}
                            </button>
                        </div>
                    )}
                </div>
            </section>
        </>
    );
}
